import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  AlertTriangle, Plus, Pencil, Trash2, FileSpreadsheet, FileText, Eye, Search, ClipboardList,
} from "lucide-react";
import { api, downloadExport, fileViewUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge2, statusColor } from "@/components/ComplianceBadge";
import { FileDropzone } from "@/components/FileDropzone";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const TYPES = ["Desvio", "Quase-Acidente", "Sem Afastamento", "Com Afastamento"];
const SEVERITY = ["Leve", "Média", "Alta", "Crítica"];
const STATUS = ["Registrado", "Em Investigação", "Plano de Ação", "Concluído"];
const ISHIKAWA_M = ["Método", "Máquina", "Mão de obra", "Material", "Meio ambiente", "Medição"];

const empty = () => ({
  title: "", date_time: "", type: "Desvio", location: "", description: "", severity: "Leve",
  status: "Registrado", days_lost: 0, photo_id: null, photo_name: null,
  five_whys: ["", "", "", "", ""], ishikawa: {}, action_plan: [],
});

export default function Incidents() {
  const { search } = useOutletContext() || {};
  const { canEdit, canManage } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incidents");
      setRows(data);
    } catch { toast.error("Falha ao carregar"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditing(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...empty(), ...r, five_whys: r.five_whys?.length ? r.five_whys : ["", "", "", "", ""], ishikawa: r.ishikawa || {}, action_plan: r.action_plan || [] });
    setEditing(r); setOpen(true);
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title || !form.date_time) { toast.error("Preencha título e data/hora"); return; }
    setSaving(true);
    try {
      const payload = { ...form, days_lost: Number(form.days_lost || 0), five_whys: form.five_whys.filter((w) => w) };
      if (editing) { await api.put(`/incidents/${editing.id}`, payload); toast.success("Ocorrência atualizada"); }
      else { await api.post("/incidents", payload); toast.success("Ocorrência registrada"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/incidents/${deleteId}`); toast.success("Removido"); setDeleteId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAction = () => setF("action_plan", [...form.action_plan, { what: "", who: "", deadline: "", status: "Aberto" }]);
  const setAction = (i, k, v) => setF("action_plan", form.action_plan.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const rmAction = (i) => setF("action_plan", form.action_plan.filter((_, idx) => idx !== i));

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div data-testid="incidents-page">
      <PageHeader icon={AlertTriangle} title="Gestão de Ocorrências e Incidentes" subtitle="Desvios, quase-acidentes e investigação (5 Porquês · Ishikawa · 5W2H)">
        <Button variant="outline" size="sm" onClick={() => downloadExport("incidents", "excel")} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="incidents-export-excel">
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadExport("incidents", "pdf")} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="incidents-export-pdf">
          <FileText className="mr-1.5 h-4 w-4" /> PDF
        </Button>
        {canEdit && <Button onClick={openNew} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="incidents-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Reportar</Button>}
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-slate-50 text-left">
                {["Data/Hora", "Título", "Tipo", "Severidade", "Local", "Status", "Dias perdidos", "Foto"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
                {(canEdit || canManage) && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Carregando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center"><Search className="mx-auto mb-2 h-6 w-6 text-slate-300" /><p className="text-slate-400">Nenhuma ocorrência registrada</p></td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid="incidents-row">
                  <td className="whitespace-nowrap px-4 py-3">{(r.date_time || "").replace("T", " ")}</td>
                  <td className="px-4 py-3">{r.title}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.type}</td>
                  <td className="px-4 py-3"><Badge2 color={statusColor(r.severity)}>{r.severity}</Badge2></td>
                  <td className="px-4 py-3">{r.location || "—"}</td>
                  <td className="px-4 py-3"><Badge2 color={statusColor(r.status)}>{r.status}</Badge2></td>
                  <td className="px-4 py-3 text-center">{r.days_lost || 0}</td>
                  <td className="px-4 py-3">{r.photo_id ? <a href={fileViewUrl(r.photo_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#134074] hover:underline"><Eye className="h-3.5 w-3.5" />Ver</a> : "—"}</td>
                  {(canEdit || canManage) && (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`incidents-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                      {canManage && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(r.id)} data-testid={`incidents-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[720px]" data-testid="incidents-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{editing ? "Editar" : "Nova"} ocorrência</DialogTitle></DialogHeader>
          <Tabs defaultValue="dados" className="mt-2">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="dados" data-testid="tab-dados">Dados</TabsTrigger>
              <TabsTrigger value="investigacao" data-testid="tab-investigacao">Investigação</TabsTrigger>
              <TabsTrigger value="acoes" data-testid="tab-acoes">Plano de Ação</TabsTrigger>
            </TabsList>

            <TabsContent value="dados" className="space-y-4 pt-4">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Título *</Label>
                <Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="incident-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Data e hora *</Label>
                  <Input type="datetime-local" value={form.date_time} onChange={(e) => setF("date_time", e.target.value)} data-testid="incident-datetime" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Local</Label>
                  <Input value={form.location} onChange={(e) => setF("location", e.target.value)} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setF("type", v)}><SelectTrigger data-testid="incident-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Severidade potencial</Label>
                  <Select value={form.severity} onValueChange={(v) => setF("severity", v)}><SelectTrigger data-testid="incident-severity"><SelectValue /></SelectTrigger><SelectContent>{SEVERITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setF("status", v)}><SelectTrigger data-testid="incident-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Dias perdidos</Label>
                  <Input type="number" value={form.days_lost} onChange={(e) => setF("days_lost", e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Descrição do evento</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="incident-description" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Foto / documento comprobatório</Label>
                <FileDropzone value={form.photo_id} valueName={form.photo_name} onUploaded={({ id, name }) => setForm((p) => ({ ...p, photo_id: id, photo_name: name }))} testid="incident-photo" />
              </div>
            </TabsContent>

            <TabsContent value="investigacao" className="space-y-5 pt-4">
              <div>
                <p className="mb-2 text-sm font-semibold text-[#134074]">5 Porquês</p>
                <div className="space-y-2">
                  {form.five_whys.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-16 shrink-0 text-xs font-medium text-slate-500">Por quê {i + 1}</span>
                      <Input value={w} onChange={(e) => setF("five_whys", form.five_whys.map((x, idx) => idx === i ? e.target.value : x))} data-testid={`incident-why-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-[#134074]">Diagrama de Ishikawa (Causa e Efeito)</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {ISHIKAWA_M.map((m) => (
                    <div key={m}>
                      <Label className="mb-1 block text-xs font-semibold text-slate-600">{m}</Label>
                      <Textarea rows={2} value={form.ishikawa[m] || ""} onChange={(e) => setF("ishikawa", { ...form.ishikawa, [m]: e.target.value })} data-testid={`incident-ishikawa-${m}`} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="acoes" className="space-y-3 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[#134074]">Plano de Ação (5W2H)</p>
                <Button size="sm" variant="outline" onClick={addAction} data-testid="incident-add-action"><Plus className="mr-1 h-4 w-4" /> Ação</Button>
              </div>
              {form.action_plan.length === 0 && <p className="py-4 text-center text-sm text-slate-400"><ClipboardList className="mx-auto mb-1 h-5 w-5 text-slate-300" />Nenhuma ação cadastrada</p>}
              {form.action_plan.map((a, i) => (
                <div key={i} className="rounded-lg border border-[#E2E8F0] p-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div><Label className="mb-1 block text-xs text-slate-500">O quê</Label><Input value={a.what} onChange={(e) => setAction(i, "what", e.target.value)} /></div>
                    <div><Label className="mb-1 block text-xs text-slate-500">Quem</Label><Input value={a.who} onChange={(e) => setAction(i, "who", e.target.value)} /></div>
                    <div><Label className="mb-1 block text-xs text-slate-500">Prazo</Label><Input type="date" value={a.deadline || ""} onChange={(e) => setAction(i, "deadline", e.target.value)} /></div>
                    <div><Label className="mb-1 block text-xs text-slate-500">Status</Label>
                      <Select value={a.status} onValueChange={(v) => setAction(i, "status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Aberto", "Em Andamento", "Concluído"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div className="mt-2 text-right"><Button size="sm" variant="ghost" className="text-rose-600" onClick={() => rmAction(i)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remover</Button></div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="incident-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Confirmar exclusão</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
